import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useApp } from "./AppContext";
import { useSalon } from "./SalonContext";

const StripePaymentsContext = createContext(null);

export function StripePaymentsProvider({ children }) {
  const { initializeStripeConnectAccountSession } = useApp();
  const { businessInfo } = useSalon();
  const [connectInstance, setConnectInstance] = useState(null);
  const [error, setError] = useState(null);

  const fetchClientSecret = async () => {
    const responseJson = await initializeStripeConnectAccountSession(
      businessInfo?.stripe_connect_account_id,
    );
    return responseJson.clientSecret;
  };

  useEffect(() => {
    if (
      businessInfo?.stripe_connect_account_id &&
      businessInfo?.stripe_connect_account_is_onboarded &&
      !connectInstance
    ) {
      try {
        const instance = loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: fetchClientSecret,
          appearance: {
            variables: {
              colorPrimary: "#8b5cf6",
            },
          },
        });

        setConnectInstance(instance);
      } catch (error) {
        setError(error);
        console.error("Error initializing Stripe:", error);
      }
    }
  }, [businessInfo]);

  useEffect(() => {
    if (error) {
      console.error("Detected an error in Stripe, failing silently", error);
    }
  }, [error]);

  // Memoized so consumers don't re-render on every provider render. Must sit
  // above the early return below — hooks can't be called conditionally.
  const value = useMemo(
    () => ({
      connectInstance,
      error,
    }),
    [connectInstance, error],
  );

  if (!connectInstance) {
    return null; // Or a loading component
  }

  return (
    <StripePaymentsContext.Provider value={value}>
      <ConnectComponentsProvider connectInstance={connectInstance}>
        {children}
      </ConnectComponentsProvider>
    </StripePaymentsContext.Provider>
  );
}

export const useStripePayments = () => {
  const context = useContext(StripePaymentsContext);
  if (!context) {
    throw new Error(
      "useStripePayments must be used within a StripePaymentsProvider",
    );
  }
  return context;
};
